<template>
	<h2 class="h6 mt-2 mb-1">Quick Filters</h2>
	<div class="search-pills">
		<button
			class="btn btn-outline-dark btn-icon btn-pill"
			type="button"
			data-testid="btn-drawer-toggle"
			v-adobe-data-layer="allFiltersADLConfig"
			@click="() => drawerToggle()"
		>
			<FilterIcon :width="12" :height="12" />
			<span :class="{ 'visually-hidden': !isDesktop }">All Filters</span>
			{{ filtersCountDisplay }}
		</button>
		<button
			v-for="quickLink in quickLinks"
			:key="quickLink.id"
			class="btn btn-outline-dark btn-pill d-flex gap-1 w-fit-content"
			:class="{ active: isSelected(quickLink) }"
			type="button"
			:value="quickLink.id"
			:data-testid="`quicklink-${quickLink.id}`"
			@click="toggleFilter(quickLink)"
		>
			<template v-if="quickLink.iconId">
				<svg class="lti" width="1em" height="1em" fill="currentColor" aria-hidden="true">
					<use :href="`/etc/designs/ltnext/hanko-svg/life-time-icons.svg#${quickLink.iconId}`" />
				</svg>
			</template>

			{{ quickLink.name }}
		</button>
	</div>
	<BOffcanvas v-model="drawerState" class="offcanvas-width-lg" placement="end">
		<template #header>
			<p class="h6">FILTERS</p>
			<button
				class="btn-close align-self-start"
				data-testid="close-btn"
				aria-label="close filters button"
				v-adobe-data-layer="closeFilterPanelADLConfig"
				@click="() => drawerToggle()"
			></button>
		</template>
		<div class="h-100 d-flex flex-column justify-content-between">
			<div style="overflow-y: auto">
				<template v-for="category in filterCategories" :key="category.id">
					<hr />
					<p class="h6 caption mb-2">{{ category.name }}</p>
					<button
						v-for="link in category.items"
						:key="link.id"
						class="btn btn-outline-dark btn-pill"
						:class="{ active: isSelected(link) }"
						type="button"
						:value="link.id"
						:data-testid="`link-${link.id}`"
						@click="toggleFilter(link)"
					>
						{{ link.name }}
					</button>
				</template>
			</div>
			<div class="row pt-2 border-top">
				<div class="col-md-5 mb-2 mb-md-0">
					<div class="d-grid">
						<button
							class="btn btn-outline-dark"
							type="button"
							data-testid="clear-filters"
							v-adobe-data-layer="clearFiltersADLConfig"
							@click="clearFilters()"
						>
							Clear
						</button>
					</div>
				</div>
				<div class="col-md-7">
					<div class="d-grid">
						<button
							class="btn btn-dark"
							type="button"
							data-testid="view-results"
							v-adobe-data-layer="viewFilterResultsADLConfig"
							@click="() => drawerToggle()"
						>
							View Results
						</button>
					</div>
				</div>
			</div>
		</div>
	</BOffcanvas>
</template>

<script lang="ts" setup>
import { onMounted, ref, type Ref } from 'vue';
import { storeToRefs } from 'pinia';
import FilterIcon from '@/lib/icons/FilterIcon.vue';
import { BOffcanvas } from 'bootstrap-vue-next';
import { useMediaQuery, useToggle } from '@vueuse/core';
import { useD360MapFiltersStore } from '@/stores/map-d360-filters';
import { aemServletService } from '@/services/aemServletService';
import vAdobeDataLayer from '@/directives/AdobeDataLayer';
import { logError } from '@/util/logger';
import type {
	Data360FilterCategory,
	Data360FilterItem,
	FiltersConfigResponse
} from 'Types/aemui/find-a-location/findaLocation';

const isDesktop = useMediaQuery('(min-width: 992px)');
const [drawerState, drawerToggle] = useToggle(false);

const d360MapStore = useD360MapFiltersStore();
const { clearFilters, isSelected, loadFromUrl, toggleFilter } = d360MapStore;
const { availableFilters, filtersCountDisplay } = storeToRefs(d360MapStore);

const quickLinks: Ref<Data360FilterItem[]> = ref([]);
const filterCategories: Ref<Data360FilterCategory[]> = ref([]);

const dedupe = (filters: Data360FilterItem[]) => {
	const seen = new Set();
	const output = [] as Data360FilterItem[];
	filters.forEach((filter) => {
		if (!seen.has(filter.id)) {
			seen.add(filter.id);
			output.push(filter);
		}
	});
	return output;
};

const flattenResponse = (locationSeachPillsMock: FiltersConfigResponse) => {
	let flattened: Data360FilterItem[] = [...locationSeachPillsMock.quickFilters.items];
	locationSeachPillsMock.categories.forEach((category) => {
		flattened = flattened.concat(category.items);
	});
	return dedupe(flattened);
};

const allFiltersADLConfig = {
	name: 'allFilters',
	type: 'button',
	title: 'All Filters'
};

const closeFilterPanelADLConfig = {
	name: 'closeFilterPanel',
	type: 'button',
	title: 'Close All Filters'
};

const clearFiltersADLConfig = {
	name: 'clearFilters',
	type: 'button',
	title: 'Clear'
};

const viewFilterResultsADLConfig = {
	name: 'viewResults',
	type: 'button',
	title: 'View Results'
};

onMounted(async () => {
	try {
		const response = await aemServletService.getLocatorFilterConfig();
		quickLinks.value = response.data.quickFilters.items;
		filterCategories.value = response.data.categories;
		availableFilters.value = flattenResponse(response.data);
		loadFromUrl();
	} catch (e: unknown) {
		logError('GET Locator Filter Config Error', {}, e);
	}
});

defineExpose({
	drawerState,
	quickLinks,
	filterCategories
});
</script>

<style lang="scss" scoped>
.search-pills {
	display: flex;
	flex-wrap: nowrap;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scrollbar-color: var(--kpl-border-color-translucent) transparent;
	scrollbar-width: thin;
}

.btn-pill {
	border-color: var(--kpl-border-color-translucent);
	border-radius: var(--kpl-border-radius-pill);
	margin-bottom: map-get($spacers, 1);
	margin-right: map-get($spacers, 1);
	padding-left: map-get($spacers, 2);
	padding-right: map-get($spacers, 2);
	scroll-snap-align: start;
	white-space: nowrap;

	&.active,
	&.active:focus {
		background-color: var(--kpl-btn-active-bg);
		color: var(--kpl-btn-active-color);
	}

	&:hover,
	&.active:hover {
		background-color: tint-color($dark, 15);
	}

	&:focus {
		background-color: inherit;
		color: inherit;
		outline: 0.25rem solid var(--kpl-focus-ring-color);
		outline-offset: -0.25rem;
	}
}
</style>

<style>
.offcanvas-width-lg {
	--kpl-offcanvas-width: 50vw;
	--kpl-offcanvas-min-width: 32rem;
	max-width: 576px;
}
</style>
